
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { Suspense, lazy } from "react"; // Add Suspense and lazy
import Index from "./pages/Index";
import SignIn from "./pages/SignIn";
import CoachSignIn from "./pages/CoachSignIn";
import { CoachForgotPassword } from "./pages/CoachForgotPassword";
import { CoachResetPassword } from "./pages/CoachResetPassword";
import { SpecialistRegistration } from "./pages/SpecialistRegistration";
import CaregiverRegistration from "./pages/CaregiverRegistration";
import Dashboard from "./pages/Dashboard";
import NotFound from "./pages/NotFound";
import { Toaster } from "@/components/ui/toaster";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { AdminProtectedRoute } from "@/components/auth/AdminProtectedRoute";
import { CoachProtectedRoute } from "@/components/auth/CoachProtectedRoute";
import { SleepUniversities } from "./pages/SleepUniversities";
import SleepTutorial from "./pages/SleepTutorial";
import {CaregiverProfile} from "./pages/CaregiverProfile";
import { SleepBookShelf } from "@/pages/SleepBookShelf";
import { Children } from "@/pages/Children";
import { Activities } from "@/pages/Activities";
import { EditActivity } from "@/pages/EditActivity";
import { AIAnalysis } from "@/pages/AIAnalysis";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { LanguageProvider } from './i18n/LanguageContext';
import { LandingPage } from "@/pages/LandingPage";
import { ForgotPassword } from "./pages/ForgotPassword";
import { ResetPassword } from "./pages/ResetPassword";
import { WhatsAppCommunity } from "./pages/WhatsAppCommunity";
import AdminPanel from "./pages/AdminPanel";
import AdminSignIn from "./pages/AdminSignIn";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { Analytics } from "@vercel/analytics/react";
import { RoleSelection } from "./pages/RoleSelection";
import { DashboardLoading } from "@/components/ui/loading";
import WakeWindows from "./pages/WakeWindows";
import { TermsPage } from "./pages/TermsPage";
import { PrivacyPage } from "./pages/PrivacyPage";
import { BillingSyncProvider } from "@/components/BillingSyncProvider";
import { PremiumGate } from "@/components/PremiumGate";
import Configuration from "./pages/Configuration";
import { ErrorBoundary } from "@/components/shared/ErrorBoundary";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import MySleepSchedule from "./pages/MySleepSchedule";

// Lazy load the CoachDashboard component
const CoachDashboard = lazy(() => import("./pages/CoachDashboard").then(module => ({ 
  default: module.CoachDashboard 
})));

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000,
      gcTime: 5 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 1,
    },
  },
});

export function App() {
  return (
    <LanguageProvider>
      <QueryClientProvider client={queryClient}>
        <BillingSyncProvider>
          <Router>
            <Analytics />
            <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/roleselection" element={<Index />} />
            <Route path="/signin" element={<SignIn />} />
            <Route path="/sign-in" element={<SignIn />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/resetpassword" element={<ResetPassword />} /> {/* Alias for the Supabase email links */}
            <Route path="/coach-signin" element={<CoachSignIn />} />
            <Route path="/coach-forgot-password" element={<CoachForgotPassword />} />
            <Route path="/coach-reset-password" element={<CoachResetPassword />} />
            <Route path="/coachresetpassword" element={<CoachResetPassword />} /> {/* Alias for the Supabase email links */}
            <Route path="/caregiver-registration" element={<CaregiverRegistration />} />
            <Route path="/specialist-registration" element={<SpecialistRegistration />} />
            <Route path="/admin-signin" element={<AdminSignIn />} />
            <Route 
              path="/admin" 
              element={
                <AdminProtectedRoute>
                  <AdminPanel />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/configuration"
              element={
                <AdminProtectedRoute>
                  <Configuration />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute requiredRole="caregiver">
                  <ErrorBoundary
                    fallback={(error) => (
                      <div className="min-h-screen flex items-center justify-center p-4">
                        <Card className="max-w-md w-full">
                          <CardHeader>
                            <CardTitle className="text-destructive">Dashboard Error</CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-4">
                            <p className="text-sm text-muted-foreground">
                              An error occurred while loading the dashboard.
                            </p>
                            <p className="text-xs font-mono bg-muted p-2 rounded">
                              {error.message}
                            </p>
                            <Button onClick={() => window.location.href = '/dashboard'}>
                              Reload Dashboard
                            </Button>
                          </CardContent>
                        </Card>
                      </div>
                    )}
                  >
                    <Dashboard />
                  </ErrorBoundary>
                </ProtectedRoute>
              }
            />
            <Route
              path="/children"
              element={
                <ProtectedRoute requiredRole="caregiver">
                  <Children />
                </ProtectedRoute>
              }
            />
            <Route
              path="/activities"
              element={
                <ProtectedRoute requiredRole="caregiver">
                  <Activities />
                </ProtectedRoute>
              }
            />
            <Route
              path="/edit-activity/:type/:id"
              element={
                <ProtectedRoute requiredRole="caregiver">
                  <EditActivity />
                </ProtectedRoute>
              }
            />
            <Route
              path="/library"
              element={
                <ProtectedRoute requiredRole="specialist">
                  <SleepBookShelf />
                </ProtectedRoute>
              }
            />
            <Route
              path="/coach-dashboard"
              element={
                <ProtectedRoute requiredRole="specialist">
                  <PremiumGate>
                    <Suspense fallback={<DashboardLoading />}>
                      <CoachDashboard />
                    </Suspense>
                  </PremiumGate>
                </ProtectedRoute>
              }
            />
            <Route
              path="/coach-dashboard/caregiver/:caregiverId"
              element={
                <ProtectedRoute requiredRole="specialist">
                  <PremiumGate>
                    <CaregiverProfile />
                  </PremiumGate>
                </ProtectedRoute>
              }
            />
            <Route
              path="/coach-dashboard/ai-analysis/:caregiverId"
              element={
                <ProtectedRoute requiredRole="specialist">
                  <PremiumGate>
                    <AIAnalysis />
                  </PremiumGate>
                </ProtectedRoute>
              }
            />
            <Route
              path="/wake-windows"
              element={
                <ProtectedRoute requiredRole="specialist">
                  <WakeWindows />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-sleep-schedule"
              element={
                <ProtectedRoute>
                  <MySleepSchedule />
                </ProtectedRoute>
              }
            />
            <Route path="/sleep-universities" element={<SleepUniversities />} />
            <Route path="/sleep-tutorial" element={<SleepTutorial />} />
            <Route path="/whatsapp-community" element={<WhatsAppCommunity />} />
            <Route path="/role-selection" element={<RoleSelection />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          <Toaster />
          <SpeedInsights />
        </Router>
        </BillingSyncProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </LanguageProvider>
  );
}
